import { forkJoin } from 'rxjs';import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import {
  ArticleSizeLookupDTO,
  ArticleTypeDTO,
  InspectionArticleDTO,
  InspectionArticleMeasurementCellDTO,
  InspectionArticlePOMDTO,
  InspectionArticleSizeDTO,
} from '../../../DTOs/ArticleTypeDTO';
import { ArticleSizeDTO } from '../../../DTOs/ArticleSizeDTO';
import { InspectionDetailDTO } from '../../../DTOs/InspectionDetailDTO';
import { BaseService } from '../../../Services/BaseService';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

@Component({
  selector: 'app-inspection-article-dialog',
  standalone: true,
  imports: [PrimeNgModule, CommonModule,
    FormsModule],
  templateUrl: './inspection-article-dialog.component.html',
  styleUrl: './inspection-article-dialog.component.css',
})
export class InspectionArticleDialogComponent implements OnInit, OnChanges {
  private readonly initialVisibleFilteredSizeCount = 9999;
  private readonly loadMoreFilteredSizeCount = 30;
  private readonly sizeScrollLoadThresholdPx = 120;

  @Input() inspectionIdInput: number | null = null;
  @Input() inspectionArticleIdInput: number | null = null;
  @Input() inlineMode = false;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();

  inspectionId = 0;
  inspectionArticleId: number | null = null;

  article: InspectionArticleDTO = this.createEmptyArticle();
  inspectionDraft: InspectionDetailDTO | null = null;

  articleTypeList: ArticleTypeDTO[] = [];
  articleSizeList: ArticleSizeLookupDTO[] = [];
  selectedArticleType: ArticleTypeDTO | null = null;

  selectedSizes: InspectionArticleSizeDTO[] = [];
  selectedPoms: InspectionArticlePOMDTO[] = [];
  visibleMatrixSizes: InspectionArticleSizeDTO[] = [];

  sizeSearchText = '';
  visibleFilteredSizeCount = this.initialVisibleFilteredSizeCount;
  matrixColumnStartIndex = 0;
  readonly maxVisibleMatrixSizeColumns = 6;
  mobileActiveSizeId: number | null = null;

  loading = true;

  private tempIdCounter = -1;
  private lastExternalInitKey: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private baseService: BaseService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void {
    if (this.initializeFromInputs()) {
      return;
    }

    this.route.paramMap.subscribe((params) => {
      this.inspectionId = Number(params.get('inspectionId') ?? 0);

      const articleIdParam = params.get('inspectionArticleId');
      this.inspectionArticleId = articleIdParam ? Number(articleIdParam) : null;

      this.initializePage();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['inspectionIdInput'] ||
      changes['inspectionArticleIdInput'] ||
      changes['inlineMode']
    ) {
      this.initializeFromInputs();
    }
  }

  private initializePage(): void {
    this.loading = true;

    forkJoin({
      inspection: this.baseService.getInspectionDetail(this.inspectionId),
      sizes: this.baseService.getArticleSizes(),
    }).subscribe({
      next: ({ inspection, sizes }) => {
        const storedDraft = this.getStoredInspectionDraft();
        const inspectionSource = storedDraft ?? inspection;

        this.inspectionDraft = JSON.parse(JSON.stringify(inspectionSource));
        this.articleTypeList = inspection.articleTypeList ?? [];
        this.articleSizeList = this.mapArticleSizes(sizes ?? []);

        if (this.inspectionArticleId === null) {
          this.article = this.createEmptyArticle();
        } else {
          const existingArticle = inspectionSource.inspectionArticles?.find(
            (article) => article.inspectionArticleId === this.inspectionArticleId,
          );

          if (existingArticle) {
            this.article = this.cloneArticle(existingArticle);
          } else {
            this.article = this.createEmptyArticle();
            this.notification('Article could not be loaded', 'error');
          }
        }

        this.selectedArticleType =
          this.articleTypeList.find(
            (articleType) => articleType.articleTypeId === this.article.articleTypeId,
          ) ?? null;

        this.refreshDerivedState();
        this.rebuildMeasurementMatrix();
        this.loading = false;
      },
      error: (error) => {
        console.error('❌ Error loading inspection article:', error);
        this.notification('Error loading article', 'error');
        this.loading = false;
      },
    });
  }

  saveArticle(): void {
    const validationErrors = this.validateArticle();

    if (validationErrors.length > 0) {
      this.notification(`Please check:\n\n• ${validationErrors.join('\n• ')}`, 'error');
      return;
    }

    const inspectionDraft =
      this.inspectionDraft ?? this.createEmptyInspectionDraft();

    if (!inspectionDraft.inspectionArticles) {
      inspectionDraft.inspectionArticles = [];
    }

    const articleToSave = this.cloneArticle({
      ...this.article,
      sizes: [...this.selectedSizes],
      poms: [...this.selectedPoms],
    });

    const articleIndex = inspectionDraft.inspectionArticles.findIndex(
      (article) => article.inspectionArticleId === articleToSave.inspectionArticleId,
    );

    if (articleIndex >= 0) {
      inspectionDraft.inspectionArticles[articleIndex] = articleToSave;
    } else {
      inspectionDraft.inspectionArticles.push(articleToSave);
    }

    this.persistInspectionDraft(inspectionDraft);
    this.notification('Article saved', 'success');

    if (this.inlineMode) {
      this.saved.emit();
      return;
    }

    this.goBack();
  }

  goBack(): void {
    if (this.inlineMode) {
      this.canceled.emit();
      return;
    }

    this.router.navigate(['/inspection-detail', this.inspectionId], {
      queryParams: this.getDetailQueryParams(),
    });
  }

  private initializeFromInputs(): boolean {
    if (this.inspectionIdInput === null || this.inspectionIdInput === undefined) {
      return false;
    }

    const initKey = `${this.inlineMode}:${this.inspectionIdInput}:${this.inspectionArticleIdInput ?? 'new'}`;
    if (initKey === this.lastExternalInitKey) {
      return true;
    }

    this.lastExternalInitKey = initKey;
    this.inspectionId = Number(this.inspectionIdInput ?? 0);
    this.inspectionArticleId = this.inspectionArticleIdInput ?? null;
    this.initializePage();
    return true;
  }

  onArticleTypeChange(articleType: ArticleTypeDTO | null): void {
    this.selectedArticleType = articleType?.articleTypeId ? articleType : null;

    if (!articleType?.articleTypeId) {
      this.article.articleTypeId = null;
      this.article.articleTypeTitleDe = '';
      this.article.articleTypeTitleEn = '';
      this.article.imageUrl = '';
      this.article.poms = [];
      this.article.expectedMeasurements = [];
      this.refreshDerivedState();
      return;
    }

    this.article.articleTypeId = articleType.articleTypeId;
    this.article.articleTypeTitleDe = articleType.german;
    this.article.articleTypeTitleEn = articleType.english;
    this.article.imageUrl = articleType.imageUrl ?? '';
    this.article.poms = [];
    this.article.expectedMeasurements = [];

    this.baseService.getPomsByArticleType(articleType.articleTypeId).subscribe({
      next: (poms: InspectionArticlePOMDTO[]) => {
        this.article.poms = (poms ?? []).map((pom, index) => ({
          inspectionArticlePOMId: this.getNextTempId(),
          inspectionArticleId: this.article.inspectionArticleId,
          articleTypePOMId: pom.articleTypePOMId,
          titleDe: pom.titleDe,
          titleEn: pom.titleEn,
          tolerancePlus: pom.tolerancePlus,
          toleranceMinus: pom.toleranceMinus,
          controlMeasure: pom.controlMeasure,
          sorting: pom.sorting ?? index + 1,
        }));

        this.refreshDerivedState();
        this.rebuildMeasurementMatrix();
      },
      error: (error) => {
        console.error('❌ Error loading POMs:', error);
        this.article.poms = [];
        this.article.expectedMeasurements = [];
        this.refreshDerivedState();
        this.notification('Measurement points could not be loaded', 'error');
      },
    });
  }

  toggleSize(size: ArticleSizeLookupDTO, checked: boolean): void {
    if (checked) {
      const exists = this.article.sizes.some(
        (selectedSize) => selectedSize.articleSizeId === size.articleSizeId,
      );

      if (!exists) {
        this.article.sizes.push({
          inspectionArticleSizeId: this.getNextTempId(),
          articleSizeId: size.articleSizeId,
          sizeTitle: size.title,
          sorting: size.sorting,
        });

        this.refreshDerivedState();
        this.showNewestMatrixColumns();
        this.rebuildMeasurementMatrix();
        return;
      }
    } else {
      this.article.sizes = this.article.sizes.filter(
        (selectedSize) => selectedSize.articleSizeId !== size.articleSizeId,
      );
    }

    this.refreshDerivedState();
    this.rebuildMeasurementMatrix();
  }

  removeSelectedSize(sizeId: number): void {
    const selectedSize = this.articleSizeList.find(
      (size) => size.articleSizeId === sizeId,
    );

    if (!selectedSize) {
      return;
    }

    this.toggleSize(selectedSize, false);
  }

  onSizeSearchChange(): void {
    this.visibleFilteredSizeCount = this.initialVisibleFilteredSizeCount;
  }

  onAvailableSizesScroll(event: Event): void {
    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    const remainingScrollPx =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (remainingScrollPx > this.sizeScrollLoadThresholdPx) {
      return;
    }

    const filteredCount = this.filteredArticleSizes().length;
    if (this.visibleFilteredSizeCount >= filteredCount) {
      return;
    }

    this.visibleFilteredSizeCount = Math.min(
      filteredCount,
      this.visibleFilteredSizeCount + this.loadMoreFilteredSizeCount,
    );
  }

  filteredArticleSizes(): ArticleSizeLookupDTO[] {
    const term = this.sizeSearchText.trim().toLocaleLowerCase('de');

    if (!term) {
      return this.articleSizeList;
    }

    return this.articleSizeList.filter((size) =>
      size.title.toLocaleLowerCase('de').includes(term),
    );
  }

  visibleFilteredArticleSizes(): ArticleSizeLookupDTO[] {
    return this.filteredArticleSizes().slice(0, this.visibleFilteredSizeCount);
  }

  visibleFilteredSizeRangeLabel(): string {
    const total = this.filteredArticleSizes().length;
    const visible = Math.min(this.visibleFilteredSizeCount, total);
    return `${visible} / ${total}`;
  }

  rebuildMeasurementMatrix(): void {
    const existingCells = [...this.article.expectedMeasurements];
    const matrix: InspectionArticleMeasurementCellDTO[] = [];

    for (const pom of this.selectedPoms) {
      for (const size of this.selectedSizes) {
        const existingCell = existingCells.find(
          (cell) =>
            cell.inspectionArticlePOMId === pom.inspectionArticlePOMId &&
            cell.inspectionArticleSizeId === size.inspectionArticleSizeId,
        );

        matrix.push({
          inspectionArticlePOMSizeId:
            existingCell?.inspectionArticlePOMSizeId ?? this.getNextTempId(),
          inspectionArticlePOMId: pom.inspectionArticlePOMId,
          inspectionArticleSizeId: size.inspectionArticleSizeId,
          expectedValue: existingCell?.expectedValue ?? null,
        });
      }
    }

    this.article.expectedMeasurements = matrix;
  }

  getExpectedValue(pomId: number, sizeId: number): number | null {
    const cell = this.article.expectedMeasurements.find(
      (entry) =>
        entry.inspectionArticlePOMId === pomId &&
        entry.inspectionArticleSizeId === sizeId,
    );

    return cell?.expectedValue ?? null;
  }

  setExpectedValue(pomId: number, sizeId: number, value: number | null): void {
    const cell = this.article.expectedMeasurements.find(
      (entry) =>
        entry.inspectionArticlePOMId === pomId &&
        entry.inspectionArticleSizeId === sizeId,
    );

    if (cell) {
      cell.expectedValue = value;
    }
  }

  isSizeSelected(sizeId: number): boolean {
    return this.selectedSizes.some((size) => size.articleSizeId === sizeId);
  }

  hasHiddenMatrixColumns(): boolean {
    return this.selectedSizes.length > this.maxVisibleMatrixSizeColumns;
  }

  canShowPreviousMatrixColumns(): boolean {
    return this.matrixColumnStartIndex > 0;
  }

  canShowNextMatrixColumns(): boolean {
    return (
      this.matrixColumnStartIndex + this.maxVisibleMatrixSizeColumns <
      this.selectedSizes.length
    );
  }

  showPreviousMatrixColumns(): void {
    this.matrixColumnStartIndex = Math.max(
      0,
      this.matrixColumnStartIndex - this.maxVisibleMatrixSizeColumns,
    );
    this.updateVisibleMatrixSizes();
  }

  showNextMatrixColumns(): void {
    this.matrixColumnStartIndex = Math.min(
      Math.max(0, this.selectedSizes.length - this.maxVisibleMatrixSizeColumns),
      this.matrixColumnStartIndex + this.maxVisibleMatrixSizeColumns,
    );
    this.updateVisibleMatrixSizes();
  }

  currentMatrixRangeLabel(): string {
    if (this.selectedSizes.length === 0) {
      return '0 of 0';
    }

    const start = this.matrixColumnStartIndex + 1;
    const end = Math.min(
      this.matrixColumnStartIndex + this.maxVisibleMatrixSizeColumns,
      this.selectedSizes.length,
    );

    return `${start}-${end} of ${this.selectedSizes.length}`;
  }

  selectedSizeTitleList(): string {
    if (this.selectedSizes.length === 0) {
      return 'No sizes selected';
    }

    return this.selectedSizes.map((size) => size.sizeTitle).join(', ');
  }

  activeMobileSize(): InspectionArticleSizeDTO | null {
    if (!this.mobileActiveSizeId) {
      return null;
    }

    return (
      this.selectedSizes.find(
        (size) => size.inspectionArticleSizeId === this.mobileActiveSizeId,
      ) ?? null
    );
  }

  trackByArticleSizeId(_: number, size: ArticleSizeLookupDTO): number {
    return size.articleSizeId;
  }

  trackBySelectedSizeId(_: number, size: InspectionArticleSizeDTO): number {
    return size.inspectionArticleSizeId;
  }

  trackByPomId(_: number, pom: InspectionArticlePOMDTO): number {
    return pom.inspectionArticlePOMId;
  }

  private refreshDerivedState(): void {
    this.selectedSizes = [...(this.article.sizes ?? [])].sort((a, b) => {
      if (a.sorting !== b.sorting) {
        return a.sorting - b.sorting;
      }

      return a.sizeTitle.localeCompare(b.sizeTitle, 'de');
    });

    this.selectedPoms = [...(this.article.poms ?? [])].sort(
      (a, b) => a.sorting - b.sorting,
    );

    this.article.sizes = [...this.selectedSizes];
    this.article.poms = [...this.selectedPoms];

    const maxStart = Math.max(
      0,
      this.selectedSizes.length - this.maxVisibleMatrixSizeColumns,
    );

    if (this.matrixColumnStartIndex > maxStart) {
      this.matrixColumnStartIndex = maxStart;
    }

    this.updateVisibleMatrixSizes();

    const activeMobileSizeStillExists = this.selectedSizes.some(
      (size) => size.inspectionArticleSizeId === this.mobileActiveSizeId,
    );

    if (!activeMobileSizeStillExists) {
      this.mobileActiveSizeId =
        this.selectedSizes[0]?.inspectionArticleSizeId ?? null;
    }

    const filteredCount = this.filteredArticleSizes().length;
    this.visibleFilteredSizeCount = Math.min(
      filteredCount || this.initialVisibleFilteredSizeCount,
      Math.max(
        this.initialVisibleFilteredSizeCount,
        this.visibleFilteredSizeCount,
      ),
    );
  }

  private updateVisibleMatrixSizes(): void {
    this.visibleMatrixSizes = this.selectedSizes.slice(
      this.matrixColumnStartIndex,
      this.matrixColumnStartIndex + this.maxVisibleMatrixSizeColumns,
    );
  }

  private showNewestMatrixColumns(): void {
    this.matrixColumnStartIndex = Math.max(
      0,
      this.selectedSizes.length - this.maxVisibleMatrixSizeColumns,
    );
    this.updateVisibleMatrixSizes();
  }

  private validateArticle(): string[] {
    const errors: string[] = [];

    if (!this.article.articleTypeId) {
      errors.push('Article type is required');
    }

    if (!this.article.title?.trim()) {
      errors.push('Title is required');
    }

    if (!this.article.quantity || this.article.quantity < 1) {
      errors.push('Quantity must be greater than 0');
    }

    if (!this.selectedSizes.length) {
      errors.push('Select at least one size');
    }

    return errors;
  }

  private mapArticleSizes(sizes: ArticleSizeDTO[]): ArticleSizeLookupDTO[] {
    return [...sizes]
      .sort((a, b) => a.name.localeCompare(b.name, 'de', { numeric: true }))
      .map((size, index) => ({
        articleSizeId: size.databaseId,
        title: size.name,
        sorting: index + 1,
      }));
  }

  private cloneArticle(article: InspectionArticleDTO): InspectionArticleDTO {
    return JSON.parse(JSON.stringify(article));
  }

  private createEmptyArticle(): InspectionArticleDTO {
    return {
      inspectionArticleId: this.getNextTempId(),
      inspectionId: this.inspectionId,
      title: '',
      quantity: 1,
      articleTypeId: null,
      articleTypeTitleDe: '',
      articleTypeTitleEn: '',
      imageUrl: '',
      sizes: [],
      poms: [],
      expectedMeasurements: [],
    };
  }

  private createEmptyInspectionDraft(): InspectionDetailDTO {
    return {
      inspectionId: this.inspectionId,
      customerList: [],
      customer: null,
      customerContactPersonList: [],
      customerContactPerson: null,
      factoryList: [],
      factory: null,
      factoryContactPersonList: [],
      factoryContactPerson: null,
      reportNumber: '',
      orderNumber: '',
      inspectionTypeList: [],
      inspectionType: null,
      inspectionDate: null,
      reportFinalizationDate: null,
      title: '',
      inspectionStatusId: 0,
      testingInstituteList: [],
      testingInstitute: null,
      sampleInstitute: null,
      importeurList: null,
      importeur: null,
      inspectionArticles: [],
      articleTypeList: this.articleTypeList,
      countOfMeasurements: 0,
      packingWidth: 0,
      packingHeight: 0,
      packingLength: 0,
      inspectionLevelList: [],
      inspectionLevel: null,
      createdBy: null,
      createdDate: null,
      startedBy: null,
      startedDate: null,
      inspectorList: [],
      primaryInspector: null,
      additionalInspectors: [],
      isRequirements: null,
      isPackings: null,
      isIdentialDesign: null,
      isDocument: null,
      isProductDetails: null,
      imageUrls: [],
      articleSizeList: [],
    };
  }

  private persistInspectionDraft(inspection: InspectionDetailDTO): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(
      this.getInspectionDraftStorageKey(),
      JSON.stringify(inspection),
    );
  }

  private getStoredInspectionDraft(): InspectionDetailDTO | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const rawDraft = window.sessionStorage.getItem(
      this.getInspectionDraftStorageKey(),
    );

    if (!rawDraft) {
      return null;
    }

    try {
      return JSON.parse(rawDraft) as InspectionDetailDTO;
    } catch {
      return null;
    }
  }

  private getInspectionDraftStorageKey(): string {
    return `inspection-detail-draft:${this.inspectionId}`;
  }

  private getDetailQueryParams(): Record<string, string> {
    const queryParams = this.route.snapshot.queryParamMap;
    const entries = ['search', 'status', 'skip', 'take']
      .map(key => {
        const value = queryParams.get(key);
        return value ? [key, value] as const : null;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null);

    return Object.fromEntries(entries);
  }

  private getNextTempId(): number {
    return this.tempIdCounter--;
  }

  private notification(
    message: string,
    state: 'success' | 'error' | 'info',
  ): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 300 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true },
    });
  }
}
